import { Fragment, useState, useEffect } from "react";
import {
  setUserEntry,
  customerRegister,
  UpdateUser,
} from "../../server/Customer";
import { useDispatch, useSelector } from "react-redux";
import { globalFileServer } from "../../../axiosInstance";
import { useForm } from "react-hook-form";
import Input from "../containers/InputForm";
import { validPhoneNumber } from "../../ValidFunction";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { setPreload } from "../../server/CartsOpen";

const UserEntry = ({
  parentName,
  onInlineError,
  onClearInlineError,
  onRegistered,
}) => {
  const isGuest = localStorage.getItem("userRole")?.includes("guest");

  const schema = yup
    .object({
      email:
        parentName === "signIn"
          ? yup
              .string()
              .email("כתובת מייל לא תקינה")
              .required("דואר אלקטרוני חובה")
          : yup.string().nullable(),
      firstName: yup.string().required("שם פרטי חובה"),
      lastName: yup.string().required("שם משפחה חובה"),
      id:
        parentName === "signIn"
          ? yup
              .string()
              .nullable()
              .matches(/^\d*$/, "תעודת זהות חייבת להכיל ספרות בלבד")
          : yup.string().nullable(),
      phone2:
        parentName === "signIn"
          ? yup
              .string()
              .nullable()
              .matches(validPhoneNumber, "מספר טלפון לא תקין")
          : yup.string().strip(),
      phone: yup
        .string()
        .required("חובה להכניס מספר טלפון")
        .matches(validPhoneNumber, "מספר טלפון לא תקין"),

      password:
        parentName === "signIn"
          ? yup
              .string()
              .required("חובה להכניס סיסמה")
              .min(8, "הסיסמה חייבת להכיל לפחות 8 תווים")
              .matches(
                /[a-zA-Z]/,
                "הסיסמה חייבת להכיל אותיות אנגלית (A-Z, a-z)",
              )
              .matches(
                /[0-9.,-]/,
                "הסיסמה חייבת להכיל ספרות או סימנים (0-9, ., -, ,)",
              )
              .max(72, "הסיסמה לא יכולה להכיל יותר מ-72 תווים")
          : yup.string().nullable(),

      confirmPass:
        parentName === "signIn"
          ? yup
              .string()
              .required("חובה לאשר את הסיסמה")
              .oneOf([yup.ref("password"), null], "הסיסמות אינן תואמות")
          : yup.string().nullable(),

      Verifiad:
        parentName === "signIn" ? yup.boolean() : yup.boolean().nullable(),

      VerifiadGetSms:
        parentName === "signIn" ? yup.boolean() : yup.boolean().nullable(),
    })
    .required();

  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const { preload, user } = useSelector((state) => ({
    preload: state.openCart.preload,
    user: state.user.user,
  }));

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues:
      parentName !== "signIn" && !isGuest && user
        ? {
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            phone: user.phone || "",
          }
        : {},
  });

  useEffect(() => {
    if (parentName !== "signIn" && !isGuest && user) {
      reset({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
      });
    }
  }, [user, parentName, isGuest, reset]);

  const send = async (data) => {
    if (!preload) {
      dispatch(setPreload(true));
      onClearInlineError?.();

      if (parentName === "signIn") {
        const savedAddress = JSON.parse(localStorage.getItem("tempStation"));

        const stationId =
          savedAddress?.stationId || savedAddress?.polygonId || "678430";

        const payload = {
          ...data,
          tenant: "hamazon",
          username: data.email.toLowerCase(),
          defaultLocation: stationId,
          StationId: stationId,
          addrCity: savedAddress?.city || "",
          addrStreet: savedAddress?.streetName || "",
          addrHouse: savedAddress?.houseNumber || "",
          addrFloor: savedAddress?.floor || "",
          addrApartment: savedAddress?.apartment || "",
          addrZipCode: savedAddress?.zipCode || "",
          Active: 1,
        };

        if (!data.Verifiad) {
          onInlineError?.("יש לאשר את התקנון והצהרת הגיל", "נדרש אישור");
          dispatch(setPreload(false));
          return;
        }

        try {
          await dispatch(customerRegister(payload, { suppressAlert: false }));
          onRegistered?.();
        } catch (e) {
          // Only show inline error if it's not an email conflict (409)
          // Email conflicts show SweetAlert popup instead
          if (!e.message.includes("כבר קיימת")) {
            onInlineError?.(
              e?.message || "שגיאה ברישום",
              "לא הצלחנו להשלים הרשמה",
            );
          }
        } finally {
          dispatch(setPreload(false));
        }
      } else {
        const payload = {
          ...user,
          ...data,
        };

        const { phone2, email, created, updated, tenant, id, ...cleanPayload } =
          payload;

        UpdateUser(cleanPayload)
          .then(() => {
            dispatch({ type: "UPDATE_USER", payload: cleanPayload });
          })
          .catch(() => {
            onInlineError?.("שגיאה בעדכון הפרטים", "שגיאה");
          })
          .finally(() => dispatch(setPreload(false)));
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassVisibility = () => {
    setShowConfirmPass(!showConfirmPass);
  };

  return (
    <Fragment>
      <form className="register-form" onSubmit={handleSubmit(send)}>
        {parentName === "signIn" && (
          <span className="note">שדות חובה מסומנים ב*</span>
        )}

        <div className="details-form">
          <div className="row-form">
            <Input
              type="text"
              register={register}
              label={parentName === "endShopping" ? "שם פרטי" : "שם פרטי*"}
              disabled={parentName === "endShopping"}
              errors={errors}
              name="firstName"
            />
            <Input
              type="text"
              register={register}
              label={parentName === "endShopping" ? "שם משפחה" : "שם משפחה*"}
              disabled={parentName === "endShopping"}
              errors={errors}
              name="lastName"
            />
          </div>

          <div className="row-form">
            <Input
              type="number"
              register={register}
              label={parentName === "signIn" ? "טלפון*" : "טלפון"}
              errors={errors}
              name="phone"
            />

            {parentName === "signIn" && (
              <Input
                type="number"
                register={register}
                label="טלפון נוסף"
                errors={errors}
                name="phone2"
              />
            )}
          </div>

          <div className="row-form">
            {parentName === "signIn" && (
              <div style={{ width: "100%" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  register={register}
                  errors={errors}
                  label="סיסמה*"
                  name="password"
                  toggleVisibility={togglePasswordVisibility}
                  showPassword={showPassword}
                  isPasswordField
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                />
                {passwordFocused && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--hz-muted)",
                      marginTop: "10px",
                      padding: "10px 12px",
                      backgroundColor: "var(--hz-bg)",
                      border: "1px solid var(--hz-border)",
                      borderRadius: "6px",
                      lineHeight: "1.6",
                      fontWeight: "500",
                    }}
                  >
                    <div
                      style={{
                        marginBottom: "6px",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        color: "var(--hz-navy)",
                      }}
                    >
                      דרישות הסיסמה:
                    </div>
                    <div style={{ marginLeft: "8px" }}>
                      <div style={{ marginBottom: "4px" }}>• לפחות 8 תווים</div>
                      <div style={{ marginBottom: "4px" }}>
                        • אותיות אנגלית (A-Z, a-z)
                      </div>
                      <div>• ספרות או סימנים (0-9, ., -, ,)</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {parentName === "signIn" && (
              <Input
                type={showConfirmPass ? "text" : "password"}
                register={register}
                errors={errors}
                label="אימות סיסמה*"
                name="confirmPass"
                toggleVisibility={toggleShowConfirmPassVisibility}
                showPassword={showConfirmPass}
                isPasswordField
              />
            )}
          </div>
          {parentName === "signIn" && (
            <div className="row-form">
              <Input
                type="email"
                register={register}
                label="דואר אלקטרוני*"
                errors={errors}
                name="email"
              />
              <span></span>
            </div>
          )}
          <div className="row-form">
            {parentName === "signIn" && (
              <div className="checkbox-style">
                <Input
                  type="checkbox"
                  register={register}
                  name="Verifiad"
                  label={
                    <span className="verify">
                      הנני מאשר/ת כי קראתי את{" "}
                      <a
                        target="_blank"
                        href={globalFileServer + "policy_form.pdf"}
                        rel="noreferrer"
                      >
                        תקנון
                      </a>{" "}
                      האתר, ומצהיר/ה כי אני מעל גיל 18
                    </span>
                  }
                />
              </div>
            )}
          </div>

          <div className="row-form">
            {parentName !== "endShopping" && (
              <button
                className="register-btn"
                type="submit"
                disabled={parentName !== "signIn" && !isGuest && !user}
              >
                {parentName === "signIn" ? "הרשמה >" : "עדכון"}
              </button>
            )}

            {parentName === "signIn" && (
              <button
                type="button"
                className="cancel"
                onClick={() => dispatch(setUserEntry([false, "login"]))}
              >
                כניסה ללא הרשמה&gt;
              </button>
            )}
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default UserEntry;
